import { useMutation } from "react-query";
import axios from "axios";

/**
 * Hook to fetch data from an external source (Fathom, etc.)
 *
 * Calls the processExcelForDirectDataImport API with source config.
 * The backend handles fetching from the external API, transforming data,
 * saving to GCS, and returning the preview config.
 *
 * @param {Object} options
 * @param {string} options.sourceId - The external source id (e.g., "fathom")
 * @param {string} options.apiKey - User's API key for the external service
 * @param {string} options.autoInjectRowIdKey - Key for auto-injected row IDs
 * @param {Object} [options.adapterOptions] - Optional adapter-specific options
 * @param {string} [options.contentType] - Reimport mode: the dataset being
 *   imported into, so the BE matches columns against its stored fields (and,
 *   for a stored-connection provider, checks the selection against the scope
 *   the dataset is pinned to).
 */
export const useFetchFromExternalSource = () => {
  return useMutation({
    mutationFn: async ({ sourceId, apiKey, autoInjectRowIdKey, adapterOptions = {}, contentType }) => {
      const response = await axios.post(
        "/api/content/processExcelForDirectDataImport",
        {
          source: {
            id: sourceId,
            apiKey,
            adapterOptions,
          },
          autoInjectRowIdKey,
          ...(contentType && { contentType }),
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to fetch data from source");
      }

      return response.data; // Returns: { success, config, metadata, fileNameInGcs, sampleData }
    },
  });
};
